'use client';

import { useState } from 'react';
import { tw } from '@tw';
import type { QuizLanguage, QuizSource } from '@peach/types/quizzes/generated-quiz.dto.js';
import type { EditableQuiz } from '@/components/builder/QuizFields';
import { useQuestionSwap } from '@/components/builder/useQuestionSwap';
import { questionProblem } from '@/lib/quiz/question-problem';
import s from './sub.module.css';

/** The server's own caps (`UpdateQuizBody`), so a line is never refused by a limit nobody saw. */
const TITLE_MAX = 200;
const QUESTION_MAX = 2000;
const ANSWER_MAX = 200;

interface QuizPostEditorProps<T extends EditableQuiz> {
  quiz: T;
  /** What the quiz was written from: without a brief there is no "Write this one again". */
  source: QuizSource & { language?: QuizLanguage };
  who: { firstName: string; picture: string | null };
  /** Under the name, e.g. "Quiz, 10 questions, posted 20 Oct". */
  byline: string;
  /** The button's word for a quiz not posted yet; absent, it counts the changes. */
  postLabel?: string;
  cancelLabel: string;
  /** Resolves to an error to show, or null once saved. */
  onSave: (next: T) => Promise<string | null>;
  onCancel: () => void;
}

const sameQuestion = (a: EditableQuiz['questions'][number], b: EditableQuiz['questions'][number]) =>
  a.text === b.text &&
  a.options.length === b.options.length &&
  a.options.every(
    (o, i) => o.label === b.options[i]!.label && o.isCorrect === b.options[i]!.isCorrect
  );

/**
 * A quiz edited as the post its players see (design A, chosen 2026-10-20): one question at a
 * time, every line typed over in place, and a tap on an answer makes it the right one.
 */
export function QuizPostEditor<T extends EditableQuiz>({
  quiz,
  source,
  who,
  byline,
  postLabel,
  cancelLabel,
  onSave,
  onCancel,
}: QuizPostEditorProps<T>) {
  const [draft, setDraft] = useState<T>(quiz);
  const [at, setAt] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { canSwap, swap, busy, failed } = useQuestionSwap(draft, source, setDraft);

  const total = draft.questions.length;
  const question = draft.questions[at]!;
  const problems = draft.questions.map(questionProblem);
  const problem = problems[at];
  const changes =
    (draft.title !== quiz.title ? 1 : 0) +
    draft.questions.filter((q, i) => !sameQuestion(q, quiz.questions[i]!)).length;

  const replace = (next: T['questions'][number]) =>
    setDraft({ ...draft, questions: draft.questions.map((q, i) => (i === at ? next : q)) });

  const save = async () => {
    const blocked = problems.findIndex((p) => p?.blocking);
    if (!draft.title.trim()) return setError('The quiz needs a title.');
    // The question's own note says what is wrong once it is on screen.
    if (blocked >= 0) return setAt(blocked);
    setSaving(true);
    setError(null);
    const failure = await onSave(draft);
    setSaving(false);
    if (failure) setError(failure);
  };

  const saveLabel = saving
    ? 'Saving…'
    : (postLabel ??
      (changes === 0
        ? 'No changes yet'
        : `Save ${changes} ${changes === 1 ? 'change' : 'changes'}`));

  return (
    <article className={tw(s.post, s.edPost)} aria-label="Edit quiz">
      <header className={s.postHead}>
        {who.picture ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={who.picture} alt="" className={s.postTile} />
        ) : (
          <span className={s.postTile} aria-hidden="true">
            {who.firstName.charAt(0)}
          </span>
        )}
        <p className={s.postWho}>
          {who.firstName}
          <span>{byline}</span>
        </p>
      </header>

      <label className="block">
        <span className="sr-only">Quiz title</span>
        <textarea
          className={s.edHook}
          rows={2}
          maxLength={TITLE_MAX}
          value={draft.title}
          onChange={(e) => setDraft({ ...draft, title: e.target.value })}
        />
      </label>

      <div className={s.edSegs} role="group" aria-label="Questions">
        {draft.questions.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-current={i === at ? 'step' : undefined}
            aria-label={`Question ${i + 1}${problems[i] ? ', needs a look' : ''}`}
            className={tw(i === at && s.edSegOn, problems[i] && s.edSegFlag)}
            onClick={() => setAt(i)}
          >
            <b>{i + 1}</b>
            <i />
          </button>
        ))}
      </div>

      <div className={s.band}>
        <p className={s.bandCount}>
          Question {at + 1} of {total}
        </p>
        {busy === at ? (
          <div className={tw(s.writing, 'mt-2')} role="status">
            <span className={s.spin} aria-hidden="true" />
            <p className={s.bandQ}>Writing it again</p>
          </div>
        ) : (
          <label className="block">
            <span className="sr-only">Question {at + 1}</span>
            <textarea
              className={tw(s.edQ, 'notranslate')}
              translate="no"
              rows={3}
              maxLength={QUESTION_MAX}
              value={question.text}
              onChange={(e) => replace({ ...question, text: e.target.value })}
            />
          </label>
        )}
      </div>

      {question.options.map((option, k) => (
        <div key={k} className={tw(s.edOpt, option.isCorrect && s.edOptRight)}>
          <button
            type="button"
            className={tw(s.edMark, s.press)}
            aria-pressed={option.isCorrect}
            aria-label={option.isCorrect ? 'The right answer' : 'Make this the right answer'}
            onClick={() =>
              replace({
                ...question,
                options: question.options.map((o, j) => ({ ...o, isCorrect: j === k })),
              })
            }
          >
            <CheckIcon />
          </button>
          <input
            className={tw(s.edInput, 'notranslate')}
            translate="no"
            aria-label={`Answer ${k + 1}`}
            maxLength={ANSWER_MAX}
            value={option.label}
            onChange={(e) =>
              replace({
                ...question,
                options: question.options.map((o, j) =>
                  j === k ? { ...o, label: e.target.value } : o
                ),
              })
            }
          />
          {option.isCorrect && <small className={s.edTag}>Right answer</small>}
        </div>
      ))}

      {problem && (
        <p className={tw(s.edNote, problem.blocking && s.edNoteBlock)} aria-live="polite">
          {problem.text}
        </p>
      )}
      {failed === at && (
        <p className={s.postError}>That question was not written again. Try again.</p>
      )}

      <div className={s.edTools}>
        <button
          type="button"
          className={tw(s.outline, s.press, s.edNav)}
          aria-label="Previous question"
          onClick={() => setAt((at + total - 1) % total)}
        >
          <ChevronIcon flip />
        </button>
        {canSwap && (
          <button
            type="button"
            disabled={busy !== null}
            className={tw(s.outline, s.press, 'flex-1')}
            onClick={() => void swap(at)}
          >
            Write this one again
          </button>
        )}
        <button
          type="button"
          className={tw(s.outline, s.press, s.edNav, !canSwap && 'ml-auto')}
          aria-label="Next question"
          onClick={() => setAt((at + 1) % total)}
        >
          <ChevronIcon />
        </button>
      </div>

      {error && (
        <p className={s.postError} aria-live="polite">
          {error}
        </p>
      )}
      <div className={s.composeRow}>
        <button type="button" className={tw(s.outline, s.press)} onClick={onCancel}>
          {cancelLabel}
        </button>
        <button
          type="button"
          disabled={saving || busy !== null || (!postLabel && changes === 0)}
          className={tw(s.cta, s.press)}
          onClick={() => void save()}
        >
          {saveLabel}
        </button>
      </div>
    </article>
  );
}

const CheckIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const ChevronIcon = ({ flip }: { flip?: boolean }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d={flip ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);
